import React from "react";
import { FiCalendar, FiCamera, FiMail, FiShield } from "react-icons/fi";
import { Badge, Spinner } from "components/ui";

interface AccountProfileHeaderProps {
  item: any;
  isUploading: boolean;
  setIsUploading: (value: boolean) => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  joinedLabel: React.ReactNode;
  joinedDate?: string;
  mfaEnabled: boolean;
}

const getInitials = (name?: string) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

/** Profile summary: avatar with upload affordance, name, email and membership meta. */
export const AccountProfileHeader = ({
  item,
  isUploading,
  setIsUploading,
  onFileSelect,
  onKeyDown,
  joinedLabel,
  joinedDate,
  mfaEnabled,
}: AccountProfileHeaderProps) => (
  <section className="p-5 zs-card sm:p-6">
    <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
      <div className="relative self-start shrink-0">
        {isUploading ? (
          <div className="flex items-center justify-center w-20 h-20 border rounded-full sm:w-24 sm:h-24 bg-surface-2 border-line">
            <Spinner className="w-6 h-6" />
          </div>
        ) : item.profile_pic ? (
          <div className="w-20 h-20 overflow-hidden border rounded-full sm:w-24 sm:h-24 bg-surface-2 border-line">
            <img
              src={item.profile_pic}
              alt="Uploaded Profile"
              className="object-cover w-full h-full"
              onLoad={() => setIsUploading(false)}
            />
          </div>
        ) : (
          <div
            className="flex items-center justify-center w-20 h-20 text-2xl font-semibold rounded-full sm:w-24 sm:h-24 bg-primary-soft text-primary-text font-display"
            aria-label="Default Profile"
          >
            {getInitials(item.user_nicename) || "?"}
          </div>
        )}

        <button
          type="button"
          className="absolute bottom-0 right-0 inline-flex items-center justify-center w-8 h-8 border-2 rounded-full shadow-sm cursor-pointer bg-primary text-ink-inverse border-surface hover:bg-primary-hover"
          aria-label="Change profile photo"
          title="Change profile photo"
          onClick={(event) => {
            event.preventDefault();
            document.getElementById("fileInput").click();
          }}
        >
          <FiCamera className="w-4 h-4" />
        </button>
        <input
          type="file"
          id="fileInput"
          accept="image/*"
          className="hidden"
          onChange={onFileSelect}
          onKeyDown={onKeyDown}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold truncate sm:text-xl font-display text-ink">{item.user_nicename}</h2>
          {mfaEnabled && (
            <Badge tone="success">
              <FiShield className="w-3 h-3" />
              MFA
            </Badge>
          )}
        </div>
        <p className="flex items-center min-w-0 gap-2 mt-1 text-sm text-ink-3">
          <FiMail className="w-4 h-4 shrink-0" />
          <span className="truncate">
            {window.innerWidth < 640 ? item.user_email.slice(0, 20) + "..." : item.user_email}
          </span>
        </p>
        <p className="flex items-center gap-2 mt-1 text-sm text-ink-3">
          <FiCalendar className="w-4 h-4 shrink-0" />
          <span>
            {joinedLabel} <span className="font-medium text-ink-2">{joinedDate}</span>
          </span>
        </p>
      </div>

      <p className="text-xs zs-hint sm:max-w-[180px] sm:text-right">JPEG or PNG. Square images look best.</p>
    </div>
  </section>
);
